import React from 'react';
import { connect } from 'react-redux';
import { Route, Switch } from 'react-router-dom';
import { ThemeProvider, makeStyles } from '@material-ui/core/styles';
import CircularProgress from '@material-ui/core/CircularProgress';
import routes from '../routes';
import {
  backgroundColors,
  themePrimary,
  fontColors,
} from '../material-ui-theme';
import SnackBox from '../components/SnackBox';
import ConnectionNotifier from '../components/ConnectionNotifier';
import { auth, backtests, setAuthorizationHeader } from '../api';
import storage from '../storage';
import { fetchUser } from '../actions/users';
import { addNotification } from '../actions/notifications';
import {
  getIsSelectedUserFetching,
  getIsSelectedUserOk,
  getIsSelectedUser,
  getIsSelectedUserErrorMessage,
} from '../reducers';
import { TOKEN } from '../constants/cookies';
import MasterLoadingPage from '../pages/MasterLoadingPage';
import { forcedLogout, intendedLogout } from '../utils';
import SubscriptionAppBar from '../partials/SubscriptionLimitedLayout/SubscriptionAppBar';
import SubscriptionDrawer from '../partials/SubscriptionLimitedLayout/SubscriptionDrawer';

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100vw',
    height: '100vh',
    background: backgroundColors.grey,
    display: 'flex',

    '& .header': {
      marginTop: '-12px',
    },
  },
  loading: {
    position: 'fixed',
    top: theme.spacing(7),
    left: '0',
    width: '100vw',
    display: 'flex',
    zIndex: '2000',
    [theme.breakpoints.up('sm')]: {
      top: theme.spacing(8),
    },
  },
  content: {
    flexGrow: 1,
    padding: theme.spacing(3),
    paddingLeft: 12,
    paddingRight: 12,
    overflow: 'auto',
    paddingBottom: 120,
    position: 'relative',

    [theme.breakpoints.up('md')]: {
      paddingLeft: theme.spacing(3),
      paddingRight: theme.spacing(3),
    },

    '&.padding-bottom-normal': {
      paddingBottom: theme.spacing(1),
    },

    '& .header': {
      '& header': {
        fontSize: '1.14rem',
        fontWeight: 500,
      },
      '& .actionBtn': {
        [theme.breakpoints.up('md')]: {
          textAlign: 'right',
        },
      },
    },

    '& .formHead': {
      margin: 0,
      textTransform: 'uppercase',
      fontWeight: 500,
      color: fontColors.grey,
      fontSize: '0.9rem',
    },
  },
  toolbar: theme.mixins.toolbar,
  article: {
    paddingTop: theme.spacing(1),
  },
  header: {
    fontSize: '1.2rem',
    fontWeight: 500,
  },
  actionBtn: {
    [theme.breakpoints.up('md')]: {
      textAlign: 'right',
    },
  },
  table: {
    '& thead th': {
      [theme.breakpoints.up('md')]: {
        fontWeight: 700,
        fontSize: '0.94rem',
      },
    },
    '& th': {
      [theme.breakpoints.up('md')]: {
        paddingLeft: theme.spacing(6),
        paddingRight: theme.spacing(6),
        paddingTop: theme.spacing(1),
        paddingBottom: theme.spacing(1),
        fontWeight: 700,
        fontSize: '0.88rem',
        lineHeight: '1.7rem',
      },
    },
    '& td': {
      [theme.breakpoints.up('md')]: {
        paddingLeft: theme.spacing(6),
        paddingRight: theme.spacing(6),
        paddingTop: theme.spacing(1),
        paddingBottom: theme.spacing(1),
        fontWeight: 500,
        fontSize: '0.88rem',
        lineHeight: '1.7rem',
      },
    },
  },
  loadingScreen: {
    width: '100%',
    height: '100%',
    display: 'flex',
    justifyContent: 'center',
    position: 'absolute',
    backgroundColor: 'rgba(0, 0, 0, 0.09)',
    alignItems: 'center',
    zIndex: 100,
    '& .loading': {
      backgroundColor: 'var(--color-white)',
      borderRadius: 8,
      width: 80,
      height: 80,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
    },
  },
}));

function LoadingScreen() {
  const classes = useStyles();
  return (
    <div className={classes.loadingScreen}>
      <div className="loading">
        <CircularProgress disableShrink />
      </div>
    </div>
  );
}

const paddingBottomNormalList = [
  'algos',
  'billing',
  'pnl-chart',
  'signals',
  'order-book',
];

function SubscriptionLimitedLayout({
  getRoutes,
  authLogout,
  lodaingBarRequested,
  user,
  ...restProps
}) {
  const classes = useStyles();
  const [mobileOpen, setMobileOpen] = React.useState(false);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const paddingBottomNormal =
    paddingBottomNormalList.includes(
      window.location.pathname.trim().replace('/', '')
    ) || window.location.pathname.trim().includes('signals/');

  return (
    <div className="SubscriptionLimitedLayout">
      <ThemeProvider theme={themePrimary}>
        <div className={classes.root}>
          <SubscriptionAppBar
            handleDrawerToggle={handleDrawerToggle}
            authLogout={authLogout}
            user={user}
            isMasterLayout
          />
          <SubscriptionDrawer
            {...restProps}
            routes={routes}
            handleDrawerToggle={handleDrawerToggle}
            mobileOpen={mobileOpen}
          />
          <main
            className={`${classes.content} ${
              paddingBottomNormal ? 'padding-bottom-normal' : ''
            }`}
          >
            <div className={classes.toolbar} />
            <ConnectionNotifier />
            <article className={classes.article}>
              <React.Suspense fallback={<span />}>
                <Switch>{getRoutes(routes)}</Switch>
              </React.Suspense>
            </article>
          </main>
          {/* <Notifications notificationsData={notifications} /> */}

          <SnackBox maxSnack={5} />

          {lodaingBarRequested ? <LoadingScreen /> : null}
          {/* <LoadingScreen /> */}
        </div>
      </ThemeProvider>
    </div>
  );
}

class SubscriptionLimitedLayoutBase extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      lodaingBarRequested: false,
      baseFavicon: '',
      pageLoaded: false,
    };

    this.authLogout = this.authLogout.bind(this);
    this.loadingStart = this.loadingStart.bind(this);
    this.loadingStop = this.loadingStop.bind(this);
    this.forceLogOut = this.forceLogOut.bind(this);
    this.handleAddNotification = this.handleAddNotification.bind(this);
    this.changePageTitle = this.changePageTitle.bind(this);
    this.changePageFavicon = this.changePageFavicon.bind(this);
    this.resetFavicon = this.resetFavicon.bind(this);
    this.changeFaviconStrategyState =
      this.changeFaviconStrategyState.bind(this);
    this.mounted = this.mounted.bind(this);
  }

  forceLogOut() {
    // Log off if unauthorized
    forcedLogout();
  }

  async mounted() {
    try {
      this.setState({ pageLoaded: true });

      await this.props.fetchUser();
    } catch (e) {
      if (!e) {
        return;
      }

      if (e?.toLowerCase() === 'unauthorized') {
        this.forceLogOut();
        return;
      }

      this.setState({ pageLoaded: true });

      this.handleAddNotification({
        type: 'error',
        source: 'Broker',
        message: e,
      });
    }
  }

  componentDidMount() {
    this.mounted();
  }

  componentWillUnmount() {
    this.setState({ pageLoaded: false });
  }

  authLogout() {
    auth
      .logout()
      .then(() => {
        // clear login tokens from local storage if exists
        intendedLogout();
      })
      .catch((err) => {
        if (err && err.message) {
          this.handleAddNotification({
            type: 'error',
            source: 'Logout',
            message: err.message,
          });
        } else {
          this.handleAddNotification({
            type: 'error',
            source: 'Logout',
            message: 'Undefined error',
          });
        }
      });
  }

  showResult(data) {
    setAuthorizationHeader(storage.getItem(TOKEN));

    return backtests.showResult(data);
  }

  getRoutes = () => {
    const filteredRoutes = routes.filter((route) => {
      if (
        route?.path?.includes('/subscriptions') ||
        route?.path?.includes('/billing') ||
        route?.path?.includes('/account-settings')
      ) {
        return true;
      }

      return false;
    });

    return filteredRoutes.map((props) => (
      <Route
        key={props.id}
        path={props.path}
        render={(p) => (
          <props.component
            {...p}
            loadingStart={this.loadingStart}
            loadingStop={this.loadingStop}
            showResult={this.showResult}
            forceLogOut={this.forceLogOut}
            onAddNotification={this.handleAddNotification}
            brokersDataAll={this.state.brokersDataAll}
            changePageTitle={this.changePageTitle}
            changeFaviconStrategyState={this.changeFaviconStrategyState}
            showStoredNotifications={this.showStoredNotifications}
          />
        )}
        exact={props.exact}
        strict={props.strict}
      />
    ));
  };

  loadingStart() {
    this.setState({ lodaingBarRequested: true });
  }

  loadingStop() {
    this.setState({ lodaingBarRequested: false });
  }

  handleAddNotification(data) {
    this.props.addNotification(data);
  }

  changePageTitle(title, restIcon = true) {
    if (restIcon) {
      this.changePageFavicon(require('../resources/favicons/favicon.png'));
    }

    let newTitle = 'Quantiply';

    if (title) {
      newTitle = title;
    }

    document.title = newTitle;
  }

  changePageFavicon(favicon) {
    document.getElementById('favicon').href = favicon;
  }

  changeFaviconStrategyState(stratState) {
    const statuses = {
      TRADE_ACTIVE: require('../resources/favicons/circle-safe.png'),
      TRADE_CLOSED: require('../resources/favicons/circle-danger.png'),
      READY: require('../resources/favicons/circle-warning.png'),
      NOT_RUNNING: require('../resources/favicons/circle-default.png'),
    };

    if (statuses[stratState]) {
      this.changePageFavicon(statuses[stratState]);
    } else {
      this.changePageFavicon(
        require('../resources/favicons/circle-default.png')
      );
    }
  }

  resetFavicon() {
    // this.changePageFavicon(this.state.baseFavicon);
  }

  render() {
    const { errorMsgUser } = this.props;
    const { pageLoaded } = this.state;

    return (
      <>
        {!pageLoaded ||
        (errorMsgUser && errorMsgUser.toLowerCase() === 'unauthorized') ? (
          <MasterLoadingPage />
        ) : (
          ''
        )}

        <SubscriptionLimitedLayout
          getRoutes={this.getRoutes}
          authLogout={this.authLogout}
          lodaingBarRequested={this.state.lodaingBarRequested}
          {...this.props}
          brokersDataAll={this.state.brokersDataAll}
          isStoredNotifications={this.state.isStoredNotifications}
          closeStoredNotifications={this.closeStoredNotifications}
        />
      </>
    );
  }
}

const mapStateToProps = (state) => {
  return {
    isFetchingUser: getIsSelectedUserFetching(state),
    isMountedUser: getIsSelectedUserOk(state),
    user: getIsSelectedUser(state),
    errorMsgUser: getIsSelectedUserErrorMessage(state),
  };
};

export default connect(mapStateToProps, {
  fetchUser,
  addNotification,
})(SubscriptionLimitedLayoutBase);
